import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, UserCheck, Eye, Download } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Table, Column } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { api } from '../../services/api';
import { Prospect } from '../../types';
import { formatDate, todayLocalDateString } from '../../utils/date';
import { buildCsv, downloadCsvFile } from '../../utils/exportCsv';
import { fetchAllPagesData } from '../../utils/fetchAllPages';
import { formatDateSpanishLong, formatPhoneDisplay } from '../../utils/solicitudFormDisplay';

function mapApiProspectToProspect(item: any): Prospect {
  const contactDate = item.contactDate
    ? (typeof item.contactDate === 'string' ? item.contactDate : new Date(item.contactDate).toISOString().split('T')[0])
    : '';
  return {
    id: item.id,
    name: item.name,
    email: item.email != null ? String(item.email) : '',
    phone: item.phone != null ? String(item.phone) : '',
    propertyId: item.propertyId,
    propertyName: item.properties?.name,
    contactDate,
    status: item.status || 'new',
    approvalStatus: item.approvalStatus ?? null,
    notes: item.notes,
  };
}

function filterProspectRows(
  list: Prospect[],
  searchTerm: string,
  statusFilter: 'all' | 'new' | 'contacted' | 'rejected',
): Prospect[] {
  return list.filter((prospect) => {
    const matchesSearch =
      prospect.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prospect.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || prospect.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
}

function prospectApprovalCsv(p: Prospect): string {
  if (p.approvalStatus === 'approved') return 'Aprobado';
  if (p.approvalStatus === 'rejected') return 'Desaprobado';
  return 'Pendiente';
}

function prospectPipelineStatusCsv(status: string): string {
  if (status === 'new') return 'Nuevo';
  if (status === 'contacted') return 'Contactado';
  if (status === 'rejected') return 'Rechazado';
  return status;
}

const limit = 20;

export const Prospects: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'contacted' | 'rejected'>('all');
  const [page, setPage] = useState(1);
  const [exportingCsv, setExportingCsv] = useState(false);

  const { data: res, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['prospects', 'solicitud-contestada', page],
    queryFn: () => api.getProspects({ page, limit, solicitudContestada: true }),
    staleTime: 60_000,
  });
  const prospects = useMemo(() => {
    const list = res?.data ?? [];
    return Array.isArray(list) ? list.map(mapApiProspectToProspect) : [];
  }, [res]);
  const total = res?.total ?? 0;
  const totalPages = res?.totalPages ?? 0;
  const error = queryError ? (queryError as Error)?.message ?? 'Error al cargar los prospectos' : null;

  const filteredProspects = useMemo(
    () => filterProspectRows(prospects, searchTerm, statusFilter),
    [searchTerm, statusFilter, prospects],
  );

  const handleExportCsv = useCallback(async () => {
    setExportingCsv(true);
    try {
      const raw = await fetchAllPagesData((args) =>
        api.getProspects({ page: args.page, limit: args.limit, solicitudContestada: true }),
      );
      const all = raw.map(mapApiProspectToProspect);
      const rows = filterProspectRows(all, searchTerm, statusFilter);
      const headers = [
        'ID',
        'Nombre',
        'Email',
        'Teléfono',
        'Propiedad de interés',
        'Fecha contacto',
        'Aprobación',
        'Estado',
      ];
      const csvRows = rows.map((row) => [
        row.id,
        row.name,
        row.email != null ? String(row.email) : '',
        row.phone != null && String(row.phone).trim() !== '' ? String(row.phone).trim() : '',
        row.propertyName ?? '',
        row.contactDate ? formatDate(row.contactDate) : '',
        prospectApprovalCsv(row),
        prospectPipelineStatusCsv(row.status),
      ]);
      downloadCsvFile(`prospectos-${todayLocalDateString()}.csv`, buildCsv(headers, csvRows));
    } catch (e) {
      alert((e as Error)?.message || 'Error al exportar CSV');
    } finally {
      setExportingCsv(false);
    }
  }, [searchTerm, statusFilter]);

  const columns: Column<Prospect>[] = [
    { header: 'Nombre', accessor: 'name', sortable: true },
    { header: 'Email', accessor: 'email' },
    {
      header: 'Teléfono',
      accessor: (row) => (row.phone ? formatPhoneDisplay(String(row.phone)) : '—'),
    },
    { header: 'Propiedad de Interés', accessor: 'propertyName' },
    {
      header: 'Fecha Contacto',
      accessor: (row) =>
        row.contactDate ? formatDateSpanishLong(new Date(row.contactDate)) : '—',
      sortable: true,
    },
    {
      header: 'Estado',
      accessor: (row) => {
        if (row.approvalStatus === 'approved') {
          return (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Aprobado
            </span>
          );
        } else if (row.approvalStatus === 'rejected') {
          return (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Desaprobado
            </span>
          );
        } else {
          return (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              Pendiente
            </span>
          );
        }
      },
    },
    {
      header: 'Acciones',
      accessor: (row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/rentas/prospectos/${row.id}`);
          }}
          className="inline-flex items-center justify-center p-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 text-sm transition-colors"
          title="Ver prospecto"
          aria-label="Ver prospecto"
        >
          <Eye className="w-4 h-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center space-x-3">
            <UserCheck className="w-6 h-6 text-primary shrink-0" />
            <h1 className="text-2xl font-bold text-gray-900">Prospectos</h1>
          </div>
          <Button
            variant="secondary"
            icon={Download}
            onClick={handleExportCsv}
            disabled={exportingCsv}
            className="shrink-0 self-start sm:self-auto"
          >
            {exportingCsv ? 'Exportando…' : 'Exportar CSV'}
          </Button>
        </div>
        <p className="text-sm text-gray-500 pl-9">
          Solo se muestran prospectos que ya enviaron el formulario de solicitud (solicitud con información capturada).
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">Todos los estados</option>
            <option value="new">Nuevo</option>
            <option value="contacted">Contactado</option>
            <option value="rejected">Rechazado</option>
          </select>
        </div>

        {loading && (
          <div className="flex justify-center py-8 text-gray-500">Cargando prospectos...</div>
        )}
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3">
            {error}
          </div>
        )}
        {!loading && !error && (
          <>
            <Table data={filteredProspects} columns={columns} />
            {totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

